import * as api from "./ApiClient";
import LogHelper from './LogHelper';
import { isEmbedded } from "@/utils/Utils";
import * as appShellClient from '@springboard/app-shell-client';
import { IAuthenticationProvider, AuthenticationProvider } from "./IAuthenticationProvider";
import { MSALAuthenticationProvider } from "./MSALAuthenticationProvider";
import { Auth0AuthenticationProvider } from "./Auth0AuthenticationProvider";
import { isFeatureFlagEnabled } from "@/services/featureFlagService";
import { IAccount } from "./IAccount";
import { resolveWithBase } from '@/utils/pathUtils';
import { anaplanTokenRefreshService } from "@/services/anaplanTokenRefreshService";

export interface $configInterface {
    apiBaseUrl: string;
    apiProxy_url: string;
    apiBaseUrl_dev: string;
    appInsights_Id: string | null | undefined;
    appInsights_Prefix: string;
    environment: string;
    hideWhatsNew: boolean;
    hideFluenceHelp: boolean;
    portalTitle: string;
    auth0?: {
        domain: string;
        clientId: string;
        audience: string;
        redirectUri?: string;
        logoutUrl?: string;
    };
}

export class Configuration {
    public environment: string = "default";

    public apiBaseUrl: string | null | undefined;
    public apiBaseUrl_dev: string | null | undefined;
    public apiProxy_url: string | null | undefined;
    public uiBaseUrl: string | null | undefined;
    public tenantName: string | null | undefined;
    public tenantBgColor: string | null | undefined;
    public anaplanBaseUrl: string | null | undefined;

    public $config: $configInterface | null;

    constructor() {
        this.apiBaseUrl = null;
        this.uiBaseUrl = window.location.protocol + "//" + window.location.host;

        this.tenantName = null;

        this.$config = null;
    }


}


export class AuthClient {

    // Singleton pattern
    private static instance: AuthClient | null = null;

    static configuration: Configuration = new Configuration();
    static accessToken: string | null = null;
    static apiToken: string | null = null;
    static tenantId: string | null = null;
    static account: IAccount | null = null;
    static isAnaplanLogin: boolean = false;

    state!: string;

    // Provider pattern properties
    private authProvider: IAuthenticationProvider | null = null;
    private isProviderInitialized: boolean = false;



    private constructor() {
        // Provider initialization will be done lazily when first needed
    }

    public static getInstance(): AuthClient {
        if (!AuthClient.instance) {
            AuthClient.instance = new AuthClient();
            AuthClient.setupFetchInterceptor();
        }
        return AuthClient.instance;
    }
    public AuthProvider(): IAuthenticationProvider | null {
        return this.authProvider;
    }
    private async initializeAuthProvider(): Promise<void> {
        try {
            if (AuthClient.isAnaplanLogin) {
                // No provider needed — the att cookie is auto-sent by the browser via the proxy.
                // exchangeTokenForFcr() handles the token exchange.
                this.isProviderInitialized = true;
                return;
            }

            const authMechanism = localStorage.getItem("auth");
            if (authMechanism === "auth0") {
                await this.initializeAuth0Provider();
                this.isProviderInitialized = true;
                return;
            }
            else if (authMechanism === "entra") {
                await this.initializeMSALProvider();
                this.isProviderInitialized = true;
                return;
            }
            else {
                // Check if Auth0 feature flag is enabled
                const isAuth0Enabled = await isFeatureFlagEnabled('CONS-187-Support-For-Auth0-In-FCR');

                if (isAuth0Enabled) {
                    await this.initializeAuth0Provider();
                } else {
                    await this.initializeMSALProvider();
                }

                this.isProviderInitialized = true;
            }
        } catch (error) {
            console.error('Error initializing auth provider:', error);
            this.isProviderInitialized = false;
            throw error; // Authentication provider initialization failed
        }
    }

    private async initializeAuth0Provider(): Promise<void> {
        try {
            // Load Auth0 configuration from config.json
            const config = await this.loadAuth0Config();

            this.authProvider = new Auth0AuthenticationProvider(config);
            await this.authProvider.initialize();

            console.log('Auth0 provider initialized successfully');
        } catch (error) {
            console.error('Error initializing Auth0 provider:', error);
            throw error;
        }
    }

    private async initializeMSALProvider(): Promise<void> {
        try {
            const isMultitenant = this.isMultitenant();
            const uiBaseUrl = AuthClient.configuration.uiBaseUrl || window.location.origin;

            this.authProvider = new MSALAuthenticationProvider(isMultitenant, uiBaseUrl, AuthClient.tenantId);
            await this.authProvider.initialize();

            console.log('MSAL provider initialized successfully');
        } catch (error) {
            console.error('Error initializing MSAL provider:', error);
            throw error;
        }
    }

    private async loadAuth0Config(): Promise<any> {
        try {
            // Load config from the public/config.json file
            const response = await fetch(resolveWithBase('config.json'));
            const config = await response.json();

            if (!config.auth0) {
                throw new Error('Auth0 configuration not found in config.json');
            }

            return {
                domain: config.auth0.domain,
                clientId: config.auth0.clientId,
                audience: config.auth0.audience
            };
        } catch (error) {
            console.error('Error loading Auth0 config:', error);
            throw error;
        }
    }




    static setTenant = async (tenant: api.Tenant | null) => {
        if (tenant != null) {
            AuthClient.configuration.tenantName = tenant.name;
            AuthClient.configuration.tenantBgColor = tenant.bgColor;
            const tenantUrl = tenant.webApiUrl?.trim();
            const embedded = isEmbedded();
            if (tenantUrl && !embedded) {
                AuthClient.configuration.apiBaseUrl = tenantUrl;
            } else if (embedded && AuthClient.configuration.apiProxy_url) {
                AuthClient.configuration.apiBaseUrl = AuthClient.configuration.apiProxy_url;
            }
        }
        else {
            AuthClient.configuration.tenantName = null;
            AuthClient.configuration.tenantBgColor = undefined;
            AuthClient.configuration.apiBaseUrl = undefined;
        }
    }

    overrideEnsureToken = false;
    async ensureToken(forceRefresh: boolean = false): Promise<void> {
        if (this.overrideEnsureToken)
            return;

        if (AuthClient.apiToken != null)
            return;

        // Ensure provider is initialized before proceeding
        if (!this.isProviderInitialized) {
            await this.initializeAuthProvider();
        }

        if (!this.isProviderInitialized) {
            return;
        }

        try {
            if (!forceRefresh && AuthClient.accessToken) {
                return;
            }

            // For MSAL/Auth0, acquire provider token silently first
            if (this.authProvider) {
                const result = await this.authProvider.getTokenSilent(forceRefresh);
                if (!result.success) {
                    console.error("Token could not be refreshed silently via provider:", result.error);
                    if (forceRefresh) {
                        throw new Error("Silent refresh failed: " + result.error);
                    }
                    return;
                }
                AuthClient.accessToken = result.accessToken;
                AuthClient.account = result.account;
                AuthClient.tenantId = result.tenantId;

            }

            // Exchange provider token (or att cookie for Anaplan) for FCR token
            await this.exchangeTokenForFcr();

        } catch (error: any) {
            console.error("Provider token acquisition error:", error);
            throw error;
        }
    }

    async forceLogin(): Promise<void> {
        if (this.authProvider) {
            await this.authProvider.forceLogin();
        }
        // No-op for Anaplan — login is managed by the app shell
    }

    async login(loginState: string): Promise<void> {
        if (AuthClient.apiToken != null)
            return;

        if (loginState == null)
            loginState = "";

        localStorage.setItem("FluenceLoginState", loginState);

        if (AuthClient.accessToken == null && this.authProvider) {
            await this.authProvider.login(loginState);
        }
        // No-op for Anaplan — login is managed by the app shell
    }

    async logout(): Promise<void> {
        AuthClient.apiToken = null;
        AuthClient.accessToken = null;
        AuthClient.account = null;
        AuthClient.tenantId = null;

        // Clear localStorage items
        localStorage.removeItem("tenantWebApiUrl");
        localStorage.removeItem("tenantName");

        if (this.authProvider) {
            await this.authProvider.logout();
        }
        // No-op for Anaplan — logout is managed by the app shell
    }

    private requiresInteraction(errorCode: any): boolean {
        if (!errorCode || !errorCode.length) {
            return false;
        }
        return errorCode === "consent_required" ||
            errorCode === "interaction_required" ||
            errorCode === "user_login_error" ||
            errorCode === "login_required";
    }

    private isMultitenant(): boolean {
        var url = new URL(window.location.href.toLowerCase());
        if (url.hostname == "portal-ca.fluence.app")
            return true;
        if (url.hostname == "portal-us.fluence.app")
            return true;
        if (url.hostname == "portal-eu.fluence.app")
            return true;
        if (url.hostname == "portal-ca-1.fluence.app")
            return true;
        if (url.hostname == "portal-us-1.fluence.app")
            return true;
        if (url.hostname == "portal-eu-1.fluence.app")
            return true;
        if (url.hostname == "portal.fl.avantcorp.app")
            return true;
        if (url.hostname == "portal-eu-beta.fluence.app")
            return true;
        if (url.hostname == "portal-jp-demo.fluence.app")
            return true;
        if (url.hostname == "portal-jp.fluence.app")
            return true;
        if (url.hostname == "portal-eu-demo.fluence.app")
            return true;
        if (url.hostname == "portal-can.fluence.app")
            return true;
        if (url.hostname == "portal-id.fluence.app")
            return true;
        if (url.hostname == "portal-uae.fluence.app")
            return true;
        if (url.hostname == "portal-in.fluence.app")
            return true;
        if (url.hostname == "portal-id-demo.fluence.app")
            return true;
        if (url.hostname == "portal-uae-demo.fluence.app")
            return true;
        if (url.hostname == "portal-in-demo.fluence.app")
            return true;
        if (url.hostname == "portal-eu-poc.fluence.app")
            return true;
        if (url.hostname == "portal-usa.fluence.app")
            return true;
        if (url.hostname == "portal-partner.fluence.app")
            return true;

        return false;
    }


    private async exchangeTokenForFcr(): Promise<void> {
        // Always exchange the current provider token for an FCR token
        // The provider token will be sent via Authorization header to the exchange endpoint
        try {
            const auth_client = AuthClient.getInstance();
            auth_client.overrideEnsureToken = true;
            const client = new api.SecurityClient(auth_client);
            const response = await client.exchangeToken();

            if (response.success && response.token) {
                AuthClient.accessToken = response.token;
                AuthClient.ensureUserName(response.username);
                console.log("FCR token exchange successful");
            } else {
                throw new Error("Token exchange failed: " + (response.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error exchanging provider token for FCR token:", error);
            throw error;
        }
    }


    public static ensureUserName(username?: string | null | undefined): void {
        if (AuthClient.account || !username) return;
        AuthClient.account = {
            userName: username,
            name: username,
        };
    }
    public static setupFetchInterceptor() {
        const originalFetch = window.fetch;
        window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
            try {
                const response = await originalFetch(input, init);
                if (response.status === 401) {
                    if (isEmbedded()) {
                        try {
                            await appShellClient.refreshAuthToken();
                            AuthClient.accessToken = null;
                            const authClient = AuthClient.getInstance();
                            authClient.overrideEnsureToken = false;
                            await authClient.ensureToken();
                            const newInit = { ...init };
                            const newHeaders = new Headers(newInit.headers);
                            if (AuthClient.accessToken) {
                                newHeaders.set('Authorization', `Bearer ${AuthClient.accessToken}`);
                            }
                            newInit.headers = newHeaders;
                            return await originalFetch(input, newInit);
                        } catch (refreshError) {
                            console.error("Embedded token refresh failed:", refreshError);
                            throw new Error("Unauthorized - embedded token refresh failed");
                        }
                    }

                    const authClient = AuthClient.getInstance();

                    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
                    const lowerCaseUrl = url.toLowerCase();
                    const auth0Domain = AuthClient.configuration.$config?.auth0?.domain;
                    const lowerCaseAuth0Domain = auth0Domain?.toLowerCase();
                    if (lowerCaseUrl.includes('/security/exchange') || (lowerCaseAuth0Domain && lowerCaseUrl.includes(lowerCaseAuth0Domain))) {
                        return response;
                    }

                    try {
                        authClient.overrideEnsureToken = false;
                        await authClient.ensureToken(true);
                        const newInit = { ...init };
                        const newHeaders = new Headers(newInit.headers);
                        if (AuthClient.accessToken) {
                            newHeaders.set('Authorization', `Bearer ${AuthClient.accessToken}`);
                        }
                        newInit.headers = newHeaders;
                        return await originalFetch(input, newInit);
                    } catch (refreshError) {
                        console.error("Token refresh failed:", refreshError);
                        await authClient.forceLogin();
                    }
                }

                return response;
            } catch (error) {
                throw error;
            }
        };
    }
}


export class FluenceApiClientBase {
    private authClient: AuthClient;

    constructor(authClient: AuthClient) {
        this.authClient = authClient;
    }

    protected async transformOptions(options: RequestInit): Promise<RequestInit> {
        await this.authClient.ensureToken();

        options.credentials = 'include';

        const tenantName = AuthClient.configuration.tenantName;
        const commonHeaders: Record<string, string> = {
            ...(options.headers as Record<string, string>),
            env: AuthClient.configuration.environment as string,
        };
        if (tenantName) {
            commonHeaders.Tenant = /[^\x00-\x7F]/.test(tenantName)
                ? encodeURIComponent(tenantName)
                : tenantName;
        }

        if (AuthClient.apiToken == null)
            options.headers = {
                ...commonHeaders,
                Authorization: "Bearer " + AuthClient.accessToken,
            };
        else
            options.headers = {
                ...commonHeaders,
                "x-api-token": AuthClient.apiToken,
            };
        if (LogHelper.LogId != null) {
            options.headers = {
                ...options.headers,
                LogId: LogHelper.LogId as string,
            };
        }

        return options
    }

    protected transformResult(url: string, response: Response, processor: (response: Response) => any) {
        return processor(response);
    }

    protected getBaseUrl(arg0: string, baseUrl: string | undefined): string {
        var url = AuthClient.configuration.apiBaseUrl

        if (url != null)
            return url;
        return new Configuration().apiBaseUrl as string;
    }
}
